import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { 
  Info, 
  AlertTriangle, 
  CheckCircle2, 
  Lightbulb,
  Copy,
  Check,
} from 'lucide-react';

// Callout component for info boxes
export const Callout = ({ type = 'info', children, title }) => {
  const variants = {
    info: {
      icon: <Info className="h-5 w-5" />,
      className: 'border-blue-500/50 bg-blue-500/10',
      iconColor: 'text-blue-400',
    },
    warning: {
      icon: <AlertTriangle className="h-5 w-5" />,
      className: 'border-yellow-500/50 bg-yellow-500/10',
      iconColor: 'text-yellow-400',
    },
    success: {
      icon: <CheckCircle2 className="h-5 w-5" />,
      className: 'border-green-500/50 bg-green-500/10',
      iconColor: 'text-green-400',
    },
    tip: {
      icon: <Lightbulb className="h-5 w-5" />,
      className: 'border-purple-500/50 bg-purple-500/10',
      iconColor: 'text-purple-400',
    },
  };

  const variant = variants[type] || variants.info;

  return (
    <Alert className={`my-6 ${variant.className}`}>
      <div className="flex gap-3">
        <div className={variant.iconColor}>{variant.icon}</div>
        <div className="flex-1">
          {title && <div className="font-semibold mb-1">{title}</div>}
          <AlertDescription>{children}</AlertDescription>
        </div>
      </div>
    </Alert>
  );
};

// Feature card component
export const FeatureCard = ({ title, icon, children }) => {
  return (
    <Card className="h-full bg-card/50 backdrop-blur-sm border-border/40 hover:border-purple-500/30 transition-colors">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          {icon && <span className="text-purple-400">{icon}</span>}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="text-foreground/70">
        {children}
      </CardContent>
    </Card>
  );
};

// Platform tabs for multi-platform instructions
export const PlatformTabs = ({ children }) => {
  return (
    <Tabs defaultValue="windows" className="my-6">
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="windows">Windows</TabsTrigger>
        <TabsTrigger value="macos">macOS</TabsTrigger>
        <TabsTrigger value="linux">Linux</TabsTrigger>
      </TabsList>
      {children}
    </Tabs>
  );
};

// Code block with copy button
export const CodeBlock = ({ children, className, ...props }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    const code = typeof children === 'string' ? children : children?.props?.children;
    if (code) {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="relative group my-4">
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-2 rounded-md bg-card/80 border border-border/40 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-card"
        aria-label="Copy code"
      >
        {copied ? (
          <Check className="h-4 w-4 text-green-400" />
        ) : (
          <Copy className="h-4 w-4 text-foreground/60" />
        )}
      </button>
      <pre className={className} {...props}>
        {children}
      </pre>
    </div>
  );
};

// Step component for step-by-step guides
export const Step = ({ number, title, children }) => {
  return (
    <div className="flex gap-4 my-6">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-purple-500/20 to-yellow-500/20 flex items-center justify-center border border-purple-500/30">
        <span className="text-sm font-semibold text-purple-400">{number}</span>
      </div>
      <div className="flex-1">
        {title && <h3 className="text-lg font-semibold mb-2">{title}</h3>}
        <div className="text-foreground/70">{children}</div>
      </div>
    </div>
  );
};

// Steps container
export const Steps = ({ children }) => {
  return <div className="my-8 space-y-6">{children}</div>;
};

// Video embed component
export const VideoEmbed = ({ src, title }) => {
  return (
    <div className="my-6 aspect-video rounded-lg overflow-hidden border border-border/40">
      <iframe
        src={src}
        title={title}
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
};

// Image with caption
export const Figure = ({ src, alt, caption }) => {
  return (
    <figure className="my-6">
      <img 
        src={src} 
        alt={alt} 
        className="rounded-lg border border-border/40 w-full"
      />
      {caption && (
        <figcaption className="text-sm text-foreground/60 text-center mt-2">
          {caption}
        </figcaption>
      )}
    </figure>
  );
};

// Custom MDX components mapping
export const MDXComponents = {
  // Override default elements
  h1: ({ children, id }) => (
    <h1 id={id} className="scroll-mt-20 text-4xl font-bold mb-6 mt-8">
      {children}
    </h1>
  ),
  h2: ({ children, id }) => (
    <h2 id={id} className="scroll-mt-20 text-3xl font-bold mb-4 mt-8 border-b border-border/40 pb-2">
      {children}
    </h2>
  ),
  h3: ({ children, id }) => (
    <h3 id={id} className="scroll-mt-20 text-2xl font-semibold mb-3 mt-6">
      {children}
    </h3>
  ),
  h4: ({ children, id }) => (
    <h4 id={id} className="scroll-mt-20 text-xl font-semibold mb-2 mt-4">
      {children}
    </h4>
  ),
  
  // Code blocks
  pre: CodeBlock,
  
  // Links
  a: ({ href, children }) => {
    const isExternal = href?.startsWith('http');
    const Component = isExternal ? 'a' : Link;
    const props = isExternal 
      ? { href, target: '_blank', rel: 'noopener noreferrer' }
      : { to: href };
    
    return (
      <Component 
        {...props}
        className="text-purple-400 hover:text-purple-300 underline decoration-purple-400/30 hover:decoration-purple-400 transition-colors"
      >
        {children}
      </Component>
    );
  },
  
  // Custom components
  Callout,
  FeatureCard,
  PlatformTabs,
  TabsContent,
  Step,
  Steps,
  VideoEmbed,
  Figure,
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
};

export default MDXComponents;

// Export individual components for direct imports
export { TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
